import React, { useState, useEffect, useCallback } from "react";
import MainLayout from "../components/layout/MainLayout";
import { API_BASE_URL } from "../config";
import {
  BarChart3, Globe, FileText, Zap, RefreshCw, ExternalLink,
  TrendingUp, TrendingDown, AlertCircle, CheckCircle, Clock,
  Search, PenTool, ArrowRight, ChevronRight
} from "lucide-react";

// ─── Types ──────────────────────────────────────────────────────────────

interface SiteStats {
  totalPages: number;
  publishedArticles: number;
  draftArticles: number;
  scheduledArticles: number;
  lastPublishDate: string | null;
  lastScanDate: string | null;
}

interface CompetitorPage {
  url: string;
  title: string;
  h1: string;
  wordCount: number;
  lastModified: string | null;
  category: string;
}

interface CompetitorData {
  domain: string;
  totalPages: number;
  lastScanned: string | null;
  categories: Record<string, number>;
  recentPages: CompetitorPage[];
}

interface ContentGap {
  topic: string;
  competitorsCovering: string[];
  estimatedDifficulty: "low" | "medium" | "high";
  suggestedType: string;
  priority: number;
}

interface ContentQueueItem {
  id: string;
  title: string;
  type: "blogg" | "guide" | "jamforelse" | "belopp" | "lender-profil";
  status: "idea" | "generating" | "draft" | "review" | "approved" | "scheduled" | "published";
  keywords: string[];
  publishDate: string | null;
  createdAt: string;
  wordCount: number | null;
}

// ─── Constants ──────────────────────────────────────────────────────────

const TABS = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "competitors", label: "Konkurrenter", icon: Globe },
  { id: "content", label: "Content Queue", icon: FileText },
  { id: "generator", label: "Generator", icon: Zap },
] as const;

type TabId = typeof TABS[number]["id"];

const STATUS_COLORS: Record<string, string> = {
  idea: "bg-gray-100 text-gray-700",
  generating: "bg-yellow-100 text-yellow-800",
  draft: "bg-blue-100 text-blue-700",
  review: "bg-purple-100 text-purple-700",
  approved: "bg-emerald-100 text-emerald-700",
  scheduled: "bg-indigo-100 text-indigo-700",
  published: "bg-green-100 text-green-700",
};

const STATUS_LABELS: Record<string, string> = {
  idea: "Idé",
  generating: "Genererer...",
  draft: "Udkast",
  review: "Til review",
  approved: "Godkendt",
  scheduled: "Planlagt",
  published: "Publiceret",
};

const DIFFICULTY_COLORS: Record<string, string> = {
  low: "bg-green-100 text-green-700",
  medium: "bg-yellow-100 text-yellow-700",
  high: "bg-red-100 text-red-700",
};

const COMPETITORS = [
  "comparia.se",
  "finansvalp.se",
  "mystep.se",
  "bank.se",
  "snabbpengar.se",
];

// ─── Component ──────────────────────────────────────────────────────────

export default function SvenskalånSeoPage() {
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Data states
  const [siteStats, setSiteStats] = useState<SiteStats>({
    totalPages: 8,
    publishedArticles: 0,
    draftArticles: 0,
    scheduledArticles: 0,
    lastPublishDate: null,
    lastScanDate: null,
  });
  const [competitors, setCompetitors] = useState<CompetitorData[]>([]);
  const [contentGaps, setContentGaps] = useState<ContentGap[]>([]);
  const [contentQueue, setContentQueue] = useState<ContentQueueItem[]>([]);

  // ─── API Calls ──────────────────────────────────────────────────────

  const fetchCompetitorData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/seo/competitors/data`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.competitors) setCompetitors(data.competitors);
        if (data.gaps) setContentGaps(data.gaps);
        if (data.stats) setSiteStats(data.stats);
      }
    } catch (err) {
      console.error("Failed to fetch competitor data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchContentQueue = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/seo/content/queue`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.queue) setContentQueue(data.queue);
      }
    } catch (err) {
      console.error("Failed to fetch content queue:", err);
    }
  }, []);

  const triggerCompetitorScan = async () => {
    try {
      setScanning(true);
      setError(null);
      const res = await fetch(`${API_BASE_URL}/api/seo/competitors/scan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domains: COMPETITORS }),
      });
      if (res.ok) {
        // Refresh data after scan
        await fetchCompetitorData();
      } else {
        setError("Scan fejlede. Prøv igen.");
      }
    } catch (err) {
      setError("Kunne ikke forbinde til API'en.");
    } finally {
      setScanning(false);
    }
  };

  useEffect(() => {
    fetchCompetitorData();
    fetchContentQueue();
  }, [fetchCompetitorData, fetchContentQueue]);

  // ─── Render Tabs ──────────────────────────────────────────────────────

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex gap-6" aria-label="SEO Pipeline Tabs">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? "border-indigo-600 text-indigo-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <Icon size={16} />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle size={16} />
            {error}
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">✕</button>
          </div>
        )}

        {/* Tab Content */}
        {activeTab === "overview" && <OverviewTab stats={siteStats} contentQueue={contentQueue} />}
        {activeTab === "competitors" && (
          <CompetitorsTab
            competitors={competitors}
            gaps={contentGaps}
            loading={loading}
            scanning={scanning}
            onScan={triggerCompetitorScan}
            onRefresh={fetchCompetitorData}
          />
        )}
        {activeTab === "content" && (
          <ContentQueueTab
            queue={contentQueue}
            onRefresh={fetchContentQueue}
          />
        )}
        {activeTab === "generator" && <GeneratorTab />}
      </div>
    </MainLayout>
  );
}

// ─── Overview Tab ─────────────────────────────────────────────────────

function OverviewTab({ stats, contentQueue }: { stats: SiteStats; contentQueue: ContentQueueItem[] }) {
  const statCards = [
    { label: "Live sider", value: stats.totalPages, icon: Globe, color: "text-blue-600 bg-blue-50" },
    { label: "Publicerede artikler", value: stats.publishedArticles, icon: CheckCircle, color: "text-green-600 bg-green-50" },
    { label: "Udkast", value: stats.draftArticles, icon: FileText, color: "text-yellow-600 bg-yellow-50" },
    { label: "Planlagt", value: stats.scheduledArticles, icon: Clock, color: "text-indigo-600 bg-indigo-50" },
  ];

  const recentItems = contentQueue.slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.label} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">{card.label}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{card.value}</p>
                </div>
                <div className={`p-3 rounded-xl ${card.color}`}>
                  <Icon size={20} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Site Health */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Site Status</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Vercel Deployment</span>
              <span className="flex items-center gap-1.5 text-sm font-medium text-green-600">
                <CheckCircle size={14} /> Live
              </span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Seneste publicering</span>
              <span className="text-sm text-gray-900 font-medium">
                {stats.lastPublishDate || "Ingen endnu"}
              </span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Seneste konkurrent-scan</span>
              <span className="text-sm text-gray-900 font-medium">
                {stats.lastScanDate || "Aldrig"}
              </span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-gray-600">GitHub Repo</span>
              <a
                href="https://github.com/afimakfbr/svenskalaan.se"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800 font-medium"
              >
                afimakfbr/svenskalaan.se <ExternalLink size={12} />
              </a>
            </div>
          </div>
        </div>

        {/* Recent Content Queue */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-900">Content Queue</h3>
            <span className="text-xs text-gray-400">{contentQueue.length} items</span>
          </div>
          {recentItems.length === 0 ? (
            <div className="text-center py-8">
              <FileText size={32} className="mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">Ingen content i queue endnu</p>
              <p className="text-xs text-gray-400 mt-1">Kør en konkurrent-scan for at generere forslag</p>
            </div>
          ) : (
            <div className="space-y-2">
              {recentItems.map((item) => (
                <div key={item.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{item.title}</p>
                    <p className="text-xs text-gray-400">{item.type}</p>
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${STATUS_COLORS[item.status] || "bg-gray-100 text-gray-600"}`}>
                    {STATUS_LABELS[item.status] || item.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Content Strategy Summary */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-100 p-6">
        <h3 className="text-base font-semibold text-gray-900 mb-3">📋 Content Strategi — Næste skridt</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-white rounded-lg shadow-sm">
              <Search size={16} className="text-indigo-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">1. Scan konkurrenter</p>
              <p className="text-xs text-gray-500 mt-0.5">Find content gaps og muligheder</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="p-2 bg-white rounded-lg shadow-sm">
              <PenTool size={16} className="text-purple-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">2. Generer indhold</p>
              <p className="text-xs text-gray-500 mt-0.5">AI skriver udkast med YMYL compliance</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="p-2 bg-white rounded-lg shadow-sm">
              <ArrowRight size={16} className="text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">3. Publicer</p>
              <p className="text-xs text-gray-500 mt-0.5">Review → godkend → schedule → live</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Competitors Tab ──────────────────────────────────────────────────

function CompetitorsTab({
  competitors,
  gaps,
  loading,
  scanning,
  onScan,
  onRefresh,
}: {
  competitors: CompetitorData[];
  gaps: ContentGap[];
  loading: boolean;
  scanning: boolean;
  onScan: () => void;
  onRefresh: () => void;
}) {
  return (
    <div className="space-y-6">
      {/* Action Bar */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Konkurrent-analyse</h2>
          <p className="text-sm text-gray-500 mt-0.5">5 konkurrenter monitoreres — scan for at opdatere data</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={onRefresh}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Opdater
          </button>
          <button
            onClick={onScan}
            disabled={scanning}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {scanning ? (
              <>
                <RefreshCw size={14} className="animate-spin" />
                Scanner...
              </>
            ) : (
              <>
                <Search size={14} />
                Kør Scan
              </>
            )}
          </button>
        </div>
      </div>

      {/* Competitor Cards */}
      {competitors.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {competitors.map((comp) => (
            <div key={comp.domain} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Globe size={16} className="text-gray-400" />
                  <h3 className="text-sm font-semibold text-gray-900">{comp.domain}</h3>
                </div>
                <a
                  href={`https://${comp.domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-indigo-600"
                >
                  <ExternalLink size={14} />
                </a>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Sider fundet</span>
                  <span className="font-medium text-gray-900">{comp.totalPages}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Sidst scannet</span>
                  <span className="font-medium text-gray-900">
                    {comp.lastScanned ? new Date(comp.lastScanned).toLocaleDateString("da-DK") : "Aldrig"}
                  </span>
                </div>
                {Object.keys(comp.categories).length > 0 && (
                  <div className="pt-2 border-t border-gray-100">
                    <p className="text-xs text-gray-400 mb-1.5">Kategorier</p>
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(comp.categories).slice(0, 5).map(([cat, count]) => (
                        <span key={cat} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                          {cat} ({count})
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Globe size={40} className="mx-auto text-gray-300 mb-3" />
          <h3 className="text-base font-medium text-gray-900">Ingen konkurrent-data endnu</h3>
          <p className="text-sm text-gray-500 mt-1 mb-4">Klik "Kør Scan" for at analysere 5 svenske lån-sider</p>
          <button
            onClick={onScan}
            disabled={scanning}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Search size={14} />
            Kør første scan
          </button>
        </div>
      )}

      {/* Content Gaps */}
      {gaps.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="p-5 border-b border-gray-100">
            <h3 className="text-base font-semibold text-gray-900">Content Gaps</h3>
            <p className="text-sm text-gray-500 mt-0.5">Emner som konkurrenter dækker, men vi ikke gør</p>
          </div>
          <div className="divide-y divide-gray-100">
            {gaps.map((gap, i) => (
              <div key={i} className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900">{gap.topic}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${DIFFICULTY_COLORS[gap.estimatedDifficulty]}`}>
                      {gap.estimatedDifficulty}
                    </span>
                    <span className="text-xs text-gray-400">
                      {gap.competitorsCovering.length} konkurrenter dækker dette
                    </span>
                  </div>
                </div>
                <button className="flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 px-3 py-1.5 rounded-lg hover:bg-indigo-50 transition-colors">
                  Opret artikel <ChevronRight size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Content Queue Tab ────────────────────────────────────────────────

function ContentQueueTab({
  queue,
  onRefresh,
}: {
  queue: ContentQueueItem[];
  onRefresh: () => void;
}) {
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const filteredQueue = statusFilter === "all"
    ? queue
    : queue.filter((item) => item.status === statusFilter);

  return (
    <div className="space-y-6">
      {/* Filter Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white text-gray-700 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="all">Alle status</option>
            <option value="idea">Idéer</option>
            <option value="draft">Udkast</option>
            <option value="review">Til review</option>
            <option value="scheduled">Planlagt</option>
            <option value="published">Publiceret</option>
          </select>
          <span className="text-sm text-gray-400">{filteredQueue.length} items</span>
        </div>
        <button
          onClick={onRefresh}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw size={14} />
          Opdater
        </button>
      </div>

      {/* Queue Table */}
      {filteredQueue.length > 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Titel</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Type</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Status</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Publicering</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Ord</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredQueue.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 transition-colors cursor-pointer">
                  <td className="px-5 py-3">
                    <p className="text-sm font-medium text-gray-900">{item.title}</p>
                    <div className="flex gap-1 mt-1">
                      {item.keywords.slice(0, 3).map((kw) => (
                        <span key={kw} className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">
                          {kw}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-600">{item.type}</td>
                  <td className="px-5 py-3">
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${STATUS_COLORS[item.status]}`}>
                      {STATUS_LABELS[item.status] || item.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-600">
                    {item.publishDate ? new Date(item.publishDate).toLocaleDateString("da-DK") : "—"}
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-600">
                    {item.wordCount ? item.wordCount.toLocaleString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <FileText size={40} className="mx-auto text-gray-300 mb-3" />
          <h3 className="text-base font-medium text-gray-900">Content queue er tom</h3>
          <p className="text-sm text-gray-500 mt-1">Kør en konkurrent-scan eller brug Generator-tabben til at oprette nyt indhold</p>
        </div>
      )}
    </div>
  );
}

// ─── Generator Tab ────────────────────────────────────────────────────

function GeneratorTab() {
  const [articleType, setArticleType] = useState("guide");
  const [topic, setTopic] = useState("");
  const [keywords, setKeywords] = useState("");
  const [tone, setTone] = useState("professional");
  const [targetLength, setTargetLength] = useState("1500");
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);

  const handleGenerate = async () => {
    if (!topic.trim()) return;
    try {
      setGenerating(true);
      setPreview(null);
      const res = await fetch(`${API_BASE_URL}/api/seo/content/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: articleType,
          topic: topic.trim(),
          keywords: keywords.split(",").map((k) => k.trim()).filter(Boolean),
          tone,
          targetLength: parseInt(targetLength),
        }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.content) setPreview(data.content);
      }
    } catch (err) {
      console.error("Generation failed:", err);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Form */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Ny artikel</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Artikeltype</label>
              <select
                value={articleType}
                onChange={(e) => setArticleType(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="guide">Guide (uddybende)</option>
                <option value="blogg">Blogindlæg</option>
                <option value="jamforelse">Jämförelse</option>
                <option value="belopp">Beløbsside (Låna X kr)</option>
                <option value="lender-profil">Långivar-recension</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Emne / Titel</label>
              <input
                type="text"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="f.eks. Lån utan UC — komplett guide 2026"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Keywords (komma-separeret)</label>
              <input
                type="text"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                placeholder="lån utan uc, bisnode, kreditupplysning"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tone</label>
                <select
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="professional">Professionel</option>
                  <option value="friendly">Venlig / tilgængelig</option>
                  <option value="authoritative">Autoritativ / ekspert</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mål-længde (ord)</label>
                <select
                  value={targetLength}
                  onChange={(e) => setTargetLength(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="800">~800 ord (kort)</option>
                  <option value="1500">~1.500 ord (standard)</option>
                  <option value="2500">~2.500 ord (lang)</option>
                  <option value="4000">~4.000 ord (udtømmende)</option>
                </select>
              </div>
            </div>
            <button
              onClick={handleGenerate}
              disabled={generating || !topic.trim()}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {generating ? (
                <>
                  <RefreshCw size={14} className="animate-spin" />
                  Genererer med Claude...
                </>
              ) : (
                <>
                  <Zap size={14} />
                  Generer udkast
                </>
              )}
            </button>
          </div>
        </div>

        {/* Preview */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Preview</h3>
          {preview ? (
            <div className="prose prose-sm max-w-none overflow-y-auto max-h-[500px] text-gray-700">
              <pre className="whitespace-pre-wrap text-sm font-sans">{preview}</pre>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <PenTool size={32} className="text-gray-300 mb-3" />
              <p className="text-sm text-gray-500">Udfyld formularen og klik "Generer udkast"</p>
              <p className="text-xs text-gray-400 mt-1">Claude Sonnet 4 via AWS Bedrock</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
